import React, { useEffect, useRef, useState } from 'react';
import { publicAPI } from '../../services/api';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

let scriptPromise = null;
const loadGoogleScript = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = GSI_SRC;
      s.async = true;
      s.onload = resolve;
      s.onerror = () => { scriptPromise = null; reject(new Error('Google script failed to load')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
};

let cachedClientId;

// Google's own "Continue with Google" button. `onCredential(idToken)` fires after the user
// picks an account; the parent sends that token to our server. Renders nothing when Google
// sign-in isn't configured on the server or the script is blocked, so the page still works.
const GoogleButton = ({ onCredential, text = 'continue_with', disabled = false }) => {
  const holder = useRef(null);
  const callbackRef = useRef(onCredential);
  const [ready, setReady] = useState(false);
  callbackRef.current = onCredential;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (cachedClientId === undefined) {
          const { data } = await publicAPI.getGoogleConfig();
          cachedClientId = data.clientId || null;
        }
        if (!cachedClientId) return;
        await loadGoogleScript();
        if (cancelled || !holder.current) return;
        window.google.accounts.id.initialize({
          client_id: cachedClientId,
          callback: (resp) => callbackRef.current?.(resp.credential),
        });
        const width = Math.min(holder.current.offsetWidth || 320, 400);
        window.google.accounts.id.renderButton(holder.current, {
          type: 'standard', theme: 'outline', size: 'large', text, shape: 'rectangular', width,
        });
        setReady(true);
      } catch {
        /* Google unavailable — password/email flow still works */
      }
    })();
    return () => { cancelled = true; };
  }, [text]);

  return (
    <div style={{ width: '100%', display: ready ? 'block' : 'none', opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div ref={holder} style={{ display: 'flex', justifyContent: 'center', width: '100%' }} />
    </div>
  );
};

export const AuthDivider = ({ children = 'or' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0', color: 'var(--text-muted)', fontSize: 12 }}>
    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    {children}
    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
  </div>
);

export default GoogleButton;
